import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Database, RefreshCw } from "lucide-react";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { OmCard, OmHead, OmKpi, Pill, GOLD, MUTED, RED, AMBER, GREEN, NAVY, FIELD, chartTheme } from "@/components/om/OmBits";

const SEV_COLOR = { Critical: RED, High: AMBER, Medium: "#6366F1", Low: MUTED };

export default function OmDashboard() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () => api.get("/om/dashboard").then(({ data }) => setD(data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const seed = async () => {
    try {
      const { data } = await api.post("/om/seed");
      toast.success(data.seeded ? `Loaded ${data.assets} assets and ${data.spares} spare parts` : data.message);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const broadcast = async () => {
    try {
      const { data } = await api.post("/om/broadcast", { message: msg });
      toast.success(`Broadcast emailed to ${data.recipients} team member(s) — WhatsApp queued`);
      setOpen(false); setMsg(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!d) return <p className="text-[13px]" style={{ color: MUTED }}>Loading command centre…</p>;
  const k = d.kpi;

  return (
    <div data-testid="om-dashboard-page">
      <OmHead title="O&M Command Centre" subtitle="Every asset, reading, work order and fault in one operational view.">
        <Button variant="outline" onClick={seed} style={{ borderColor: `${GOLD}66`, color: GOLD, background: "transparent" }} data-testid="om-seed-btn">
          <Database className="w-4 h-4 mr-1.5" /> Load starter register
        </Button>
        <Button variant="outline" onClick={load} style={{ borderColor: `${GOLD}66`, color: GOLD, background: "transparent" }} data-testid="om-refresh-btn">
          <RefreshCw className="w-4 h-4" />
        </Button>
        <Button onClick={() => setOpen(true)} className="font-semibold" style={{ background: GOLD, color: NAVY, borderRadius: 8 }} data-testid="om-broadcast-btn">
          <Megaphone className="w-4 h-4 mr-1.5" /> WhatsApp quick alert
        </Button>
      </OmHead>

      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3">
        <OmKpi label="Total assets" value={k.assets} testid="kpi-om-assets" />
        <OmKpi label="Operational" value={k.operational} tone={GREEN} testid="kpi-om-operational" />
        <OmKpi label="Under maintenance" value={k.under_maintenance} tone={AMBER} />
        <OmKpi label="Faulty" value={k.faulty} tone={RED} testid="kpi-om-faulty" />
        <OmKpi label="PM compliance (month)" value={`${k.pm_compliance}%`} testid="kpi-om-pm" />
        <OmKpi label="Avg asset health" value={k.avg_health} tone={k.avg_health >= 80 ? GREEN : k.avg_health >= 50 ? AMBER : RED} testid="kpi-om-health" />
        <OmKpi label="Open critical" value={k.open_critical} tone={RED} testid="kpi-om-critical" />
        <OmKpi label="Open high" value={k.open_high} tone={AMBER} />
        <OmKpi label="Open medium / low" value={k.open_medium} />
        <OmKpi label="Overdue PM" value={k.overdue_pm} tone={k.overdue_pm ? RED : GREEN} testid="kpi-om-overdue" />
        <OmKpi label="SLA compliance" value={`${k.sla_compliance}%`} />
        <OmKpi label="MTTR (hours)" value={k.mttr} />
      </div>

      <div className="grid lg:grid-cols-2 gap-3 mt-3">
        <OmCard testid="om-pm-trend-card">
          <h3 className="text-white font-semibold text-[15px] mb-3">PM completion — last 6 months</h3>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.pm_trend} margin={{ top: 4, right: 8, left: -18 }}>
                <CartesianGrid stroke={chartTheme.grid} strokeDasharray="2 4" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: MUTED }} />
                <YAxis tick={{ fontSize: 10, fill: MUTED }} />
                <Tooltip contentStyle={chartTheme.tooltip} />
                <Legend wrapperStyle={{ fontSize: 11, color: MUTED }} />
                <Bar dataKey="scheduled" name="Scheduled" fill={MUTED} />
                <Bar dataKey="completed" name="Completed" fill={GOLD} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </OmCard>

        <OmCard testid="om-severity-card">
          <h3 className="text-white font-semibold text-[15px] mb-3">Open CM tickets by severity</h3>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={d.severity_split} dataKey="value" nameKey="name" innerRadius={52} outerRadius={82} paddingAngle={2}>
                  {d.severity_split.map((s) => <Cell key={s.name} fill={SEV_COLOR[s.name]} stroke="none" />)}
                </Pie>
                <Tooltip contentStyle={chartTheme.tooltip} />
                <Legend wrapperStyle={{ fontSize: 11, color: MUTED }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </OmCard>

        <OmCard testid="om-top-faults-card">
          <h3 className="text-white font-semibold text-[15px] mb-3">Top 5 assets by fault frequency</h3>
          <div style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.top_faults} layout="vertical" margin={{ left: 10, right: 16 }}>
                <CartesianGrid stroke={chartTheme.grid} strokeDasharray="2 4" />
                <XAxis type="number" tick={{ fontSize: 10, fill: MUTED }} allowDecimals={false} />
                <YAxis type="category" dataKey="asset" tick={{ fontSize: 10, fill: MUTED }} width={130} />
                <Tooltip contentStyle={chartTheme.tooltip} />
                <Bar dataKey="faults" fill={RED} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </OmCard>

        <OmCard testid="om-alert-trend-card">
          <h3 className="text-white font-semibold text-[15px] mb-3">Daily monitoring alerts — last 30 days</h3>
          <div style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d.alert_trend} margin={{ top: 4, right: 8, left: -18 }}>
                <CartesianGrid stroke={chartTheme.grid} strokeDasharray="2 4" />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: MUTED }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: MUTED }} allowDecimals={false} />
                <Tooltip contentStyle={chartTheme.tooltip} />
                <Line type="monotone" dataKey="alerts" stroke={AMBER} strokeWidth={2} dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </OmCard>
      </div>

      <div className="grid lg:grid-cols-2 gap-3 mt-3">
        <OmCard testid="om-feed-card">
          <h3 className="text-white font-semibold text-[15px] mb-2">Recent activity</h3>
          {d.feed.length === 0 ? <p className="text-[12.5px]" style={{ color: MUTED }}>Nothing logged yet.</p> : (
            <ul className="divide-y" style={{ borderColor: "rgba(255,255,255,0.07)" }}>
              {d.feed.map((f) => (
                <li key={f.id} className="py-2" data-testid="om-feed-row">
                  <div className="flex items-center gap-2">
                    <Pill tone="gold">{f.kind}</Pill>
                    <span className="text-white text-[12.5px]">{f.text}</span>
                  </div>
                  <div className="text-[10.5px] mt-0.5" style={{ color: MUTED }}>
                    {f.user} · {new Date(f.at).toLocaleString("en-IN")}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </OmCard>

        <OmCard testid="om-lowstock-card">
          <h3 className="text-white font-semibold text-[15px] mb-2">Spares at or below minimum</h3>
          {d.low_stock.length === 0 ? <p className="text-[12.5px]" style={{ color: MUTED }}>All spare levels are healthy.</p> : (
            <ul className="divide-y" style={{ borderColor: "rgba(255,255,255,0.07)" }}>
              {d.low_stock.map((s) => (
                <li key={s.id} className="py-2 flex items-center justify-between gap-3" data-testid="om-lowstock-row">
                  <span className="text-white text-[12.5px]">{s.name} <span style={{ color: MUTED }}>· {s.part_no}</span></span>
                  <Pill tone="bad">{s.stock} {s.unit} / min {s.min_stock}</Pill>
                </li>
              ))}
            </ul>
          )}
        </OmCard>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent style={{ background: "#112240", borderColor: `${GOLD}33`, color: "#fff" }}>
          <DialogHeader><DialogTitle className="text-white">Status broadcast to the O&amp;M team</DialogTitle></DialogHeader>
          <Textarea rows={4} value={msg} onChange={(e) => setMsg(e.target.value)} style={FIELD} placeholder="e.g. Chiller CH-01 back online, AHU-04 still isolated — night shift please monitor." data-testid="om-broadcast-input" />
          <p className="text-[11px]" style={{ color: MUTED }}>Sent by email now. The WhatsApp leg is queued and will deliver as soon as WhatsApp Business API credentials are added.</p>
          <DialogFooter>
            <Button onClick={broadcast} disabled={!msg.trim()} className="font-semibold" style={{ background: GOLD, color: NAVY }} data-testid="om-broadcast-send-btn">Send broadcast</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
